import { ReviewLevel } from '@scale/shared-types';
import {
  deleteAllTurnsAfterTurnIndex,
  deleteTurn,
  duplicateTurn,
  reactivateSession,
  swapTurns,
} from '@scale/shared/chat/response/validation';
import _ from 'lodash';
import { useChatStoreExtract } from 'react-scale/components/scalers/tasks/Chat/hooks/ChatStore.hooks';
import { StepCard } from 'react-scale/components/scalers/tasks/ThreadedInteraction/step/StepCard';
import { useMemo, MouseEvent } from 'react';
import { useChatLayoutStore } from 'react-scale/components/scalers/tasks/ThreadedInteraction/hooks/useChatLayoutStore';
import { twMerge } from 'tailwind-merge';
import { Typography, Divider, Icon } from '@scale/scaleui';
import { IconProp } from '@fortawesome/fontawesome-svg-core';

interface TurnActionsProps {
  readOnly?: boolean;
  turnIndex: number;
  totalTurns: number;
  isAnyTurnIncomplete: boolean;
}

/* eslint-disable complexity */
const TurnActions: React.FC<TurnActionsProps> = ({
  readOnly,
  turnIndex,
  totalTurns,
  isAnyTurnIncomplete,
}) => {
  const isDrawerOpen = useChatLayoutStore(state => state.isDrawerOpen);
  const config = useChatStoreExtract(state => state.config);
  const reviewLevel = useChatStoreExtract(state => state.reviewLevel);
  const setResponses = useChatStoreExtract(state => state.setResponses);
  const setResponsesAndCompletedSteps = useChatStoreExtract(
    state => state.setResponsesAndCompletedSteps,
  );
  const responses = useChatStoreExtract(state => state.responses);
  const allStepsCompleted = useChatStoreExtract(state => state.allStepsCompleted);

  const isInteractionIncomplete = isAnyTurnIncomplete || !allStepsCompleted;
  const isSingleTurn = Number(config.maxTurns) === 1;
  const isReviewConsensus = reviewLevel === ReviewLevel.ReviewConsensus;

  // Conditions related to adding a new last turn to the chat session
  const isLastTurn = turnIndex === totalTurns - 1;
  const showLastTurnEditingActions = config.enableLastTurnEditing && isLastTurn && !isSingleTurn;

  // Conditions related to mass turn deletion
  const showMassTurnDeletionAction =
    config.enableMassTurnDeletion && totalTurns > 1 && !isLastTurn && !isSingleTurn;

  // Conditions related to conversation editing
  const showConversationEditingActions = config.enableConversationEditing && turnIndex > 0;

  const showTurnActions =
    showConversationEditingActions || showMassTurnDeletionAction || showLastTurnEditingActions;

  const turnActions = useMemo(() => {
    const actions: {
      icon: IconProp;
      label: string;
      onClick: (e: MouseEvent<HTMLElement>) => void;
      disabled: boolean;
    }[] = [];
    if (_.isNil(totalTurns) || _.isNil(turnIndex)) {
      return actions;
    }

    if (showLastTurnEditingActions) {
      actions.push({
        icon: 'plus',
        label: 'Add a New Turn',
        onClick: (e: MouseEvent<HTMLElement>) => {
          e.stopPropagation();
          if (
            !window.confirm(
              'Are you sure you want to add a new turn? This will delete work for steps after the conversation.',
            )
          ) {
            return;
          }
          const newResponses = reactivateSession(responses);
          setResponsesAndCompletedSteps(newResponses);
        },
        disabled:
          readOnly ||
          totalTurns >= Number(config.maxTurns) ||
          isInteractionIncomplete ||
          isReviewConsensus,
      });
    }

    if (showConversationEditingActions) {
      actions.push({
        icon: 'times',
        label: 'Delete This Turn',
        onClick: (e: MouseEvent<HTMLElement>) => {
          e.stopPropagation();
          if (
            !window.confirm('Are you sure you want to delete this turn? This cannot be undone.')
          ) {
            return;
          }
          const newResponses = deleteTurn(responses, config, turnIndex);
          setResponses(newResponses);
        },
        disabled: readOnly || turnIndex < 1 || isInteractionIncomplete,
      });
      actions.push({
        icon: 'copy',
        label: 'Duplicate This Turn',
        onClick: (e: MouseEvent<HTMLElement>) => {
          e.stopPropagation();
          const newResponses = duplicateTurn(responses, config, turnIndex);
          setResponsesAndCompletedSteps(newResponses);
        },
        disabled: readOnly || turnIndex < 1 || isInteractionIncomplete || isReviewConsensus,
      });
      actions.push({
        icon: 'arrow-up',
        label: 'Switch This Turn with the Previous Turn',
        onClick: (e: MouseEvent<HTMLElement>) => {
          e.stopPropagation();
          const newResponses = swapTurns(responses, config, turnIndex - 1);
          setResponses(newResponses);
        },
        disabled: readOnly || turnIndex <= 1 || isInteractionIncomplete,
      });
      actions.push({
        icon: 'arrow-down',
        label: 'Switch This Turn with the Next Turn',
        onClick: (e: MouseEvent<HTMLElement>) => {
          e.stopPropagation();
          const newResponses = swapTurns(responses, config, turnIndex);
          setResponses(newResponses);
        },
        disabled: readOnly || turnIndex >= totalTurns - 1 || isInteractionIncomplete,
      });
    }

    if (showMassTurnDeletionAction) {
      actions.push({
        icon: 'trash',
        label: 'Delete the Entire Chat After This Turn',
        onClick: (e: MouseEvent<HTMLElement>) => {
          e.stopPropagation();
          if (
            !window.confirm(
              'Are you sure you want to delete the conversation after this turn? This cannot be undone.',
            )
          ) {
            return;
          }
          const newResponses = deleteAllTurnsAfterTurnIndex(responses, config, turnIndex);
          setResponsesAndCompletedSteps(newResponses);
        },
        disabled: readOnly || isAnyTurnIncomplete,
      });
    }

    return actions;
  }, [
    showLastTurnEditingActions,
    showConversationEditingActions,
    showMassTurnDeletionAction,
    config,
    responses,
    turnIndex,
    totalTurns,
    setResponsesAndCompletedSteps,
    readOnly,
    isAnyTurnIncomplete,
    isInteractionIncomplete,
    isReviewConsensus,
    setResponses,
  ]);

  if (!showTurnActions) {
    return null;
  }

  return (
    <StepCard.Popover
      useStepCardReference
      icon={'toolbox'}
      placement={isDrawerOpen ? 'top-end' : 'right-start'}
      modifiers={[
        {
          name: 'preventOverflow',
          options: {
            mainAxis: false,
          },
        },
        {
          name: 'offset',
          options: {
            offset: [0, 14],
          },
        },
        {
          name: 'flip',
          options: {
            fallbackPlacements: ['top-end'],
          },
        },
      ]}
    >
      <StepCard>
        <StepCard.Body className="px-0 py-0 border-t-0">
          <div className="w-full flex justify-end">
            <div className="w-full flex flex-col">
              {turnActions.map((action, index) => (
                <div key={action.label}>
                  <div
                    onClick={action.onClick}
                    className={twMerge(
                      'flex flex-row items-center w-full px-2 py-2 cursor-pointer text-sm transition-all',
                      'hover:shadow-md hover:bg-neutral-200',
                      index === 0 && 'rounded-t-md',
                      index === turnActions.length - 1 && 'rounded-b-md',
                      action.disabled
                        ? 'opacity-50 cursor-not-allowed pointer-events-none text-gray-500'
                        : 'text-gray-700',
                    )}
                  >
                    <Icon icon={action.icon} size="sm" />
                    <Typography.Subtitle3>{action.label}</Typography.Subtitle3>
                  </div>
                  {index !== turnActions.length - 1 && <Divider />}
                </div>
              ))}
            </div>
          </div>
        </StepCard.Body>
      </StepCard>
    </StepCard.Popover>
  );
};

export default TurnActions;
